import Service, { inject as service } from '@ember/service';

import { HashicorpCloudResourcemanagerPolicyBindingMemberType } from '@clients/cloud-resource-manager';
import iamFindAllRelatedRoleLocations from 'manage-access/helpers/iam-find-all-related-role-locations';
import iamPaginateArray from 'manage-access/helpers/iam-paginate-array';

import iamFetchUntilEmpty from 'manage-access/helpers/iam-fetch-until-empty';
import { parseRoleId } from '../utils/parse-role-id.ts';
import { createRoleLocationFilter } from '../utils/create-role-location-filter.ts';

// Services
import type ApiService from 'api/services/api';

// Types
import type { Filters } from 'manage-access/types/filters';
import type { FilterOptions } from 'manage-access/types/filter-otpions';
import type {
  HashicorpCloudIamGroup,
  HashicorpCloudIamPrincipal,
} from '@clients/cloud-iam';
import type { IamRoleLocation } from 'manage-access/types/iam-role-location';
import type { Organization } from 'core/services/user-context';
import type { PolicyManagerLocation } from 'manage-access/types/policy-manager-location';
import type { RoleAssignments } from 'manage-access/types/role-assignments';
import type { RolesLocation } from 'manage-access/types/roles-location';
import type { ScopeLocationLink } from 'manage-access/types/scope-location-link';
import type { HashicorpCloudResourcemanagerPolicyBindingMember } from '@clients/cloud-resource-manager';

export default class RoleAssingmentsMemberRoles extends Service {
  @service declare readonly api: ApiService;

  async fetch({
    currentRolesLocation,
    currentScopeLink,
    member,
    organization,
    policyManagerLocations,
    principal,
    rolesCurrentPage,
    rolesCurrentPageSize,
    scopeIds,
    scopeTypes,
    search,
    serviceIds,
  }: {
    currentRolesLocation: RolesLocation;
    currentScopeLink: ScopeLocationLink;
    member?: HashicorpCloudResourcemanagerPolicyBindingMember;
    organization?: Organization;
    policyManagerLocations: Array<PolicyManagerLocation>;
    principal: HashicorpCloudIamPrincipal;
    rolesCurrentPage: number;
    rolesCurrentPageSize: number;
    scopeIds: string;
    scopeTypes: string;
    search: string;
    serviceIds: string;
  }): Promise<{
    filters: Filters;
    filterOptions: FilterOptions;
    groups: Array<HashicorpCloudIamGroup>;
    groupRoleLocations: Array<IamRoleLocation>;
    inheritedRoleLocations: Array<IamRoleLocation>;
    roleAssignments: RoleAssignments;
  }> {
    const { selfRoleLocations, inheritedRoleLocations, groupRoleLocations } =
      iamFindAllRelatedRoleLocations(
        currentScopeLink,
        principal.groupIds,
        [member?.memberId],
        policyManagerLocations,
      );

    const filterOptions = {
      serviceIds: [
        ...selfRoleLocations,
        ...inheritedRoleLocations,
        ...groupRoleLocations,
      ].reduce(
        (accumulator, roleLocation) => {
          const { service } = parseRoleId(roleLocation.roleId);
          if (!service) {
            return accumulator;
          }
          accumulator[service] = (accumulator[service] || 0) + 1;
          return accumulator;
        },
        {} as Record<string, number>,
      ),
      scopeIds: [
        ...selfRoleLocations,
        ...inheritedRoleLocations,
        ...groupRoleLocations,
      ].reduce(
        (accumulator, roleLocation) => {
          if (!roleLocation?.link?.id) {
            return accumulator;
          }
          accumulator[roleLocation.link.id] =
            (accumulator[roleLocation.link.id] || 0) + 1;
          return accumulator;
        },
        {} as Record<string, number>,
      ),
      scopeTypes: [
        ...selfRoleLocations,
        ...inheritedRoleLocations,
        ...groupRoleLocations,
      ].reduce(
        (accumulator, roleLocation) => {
          if (!roleLocation?.link?.type) {
            return accumulator;
          }
          accumulator[roleLocation.link.type] =
            (accumulator[roleLocation.link.type] || 0) + 1;
          return accumulator;
        },
        {} as Record<string, number>,
      ),
    };

    const filters = {
      search,
      scopeTypes: (scopeTypes?.length ? scopeTypes.split(',') : []).filter(
        (scopeType) => {
          return Object.keys(filterOptions.scopeTypes).includes(scopeType);
        },
      ),
      scopeIds: (scopeIds?.length ? scopeIds.split(',') : []).filter(
        (scopeId) => {
          return Object.keys(filterOptions.scopeIds).includes(scopeId);
        },
      ),
      serviceIds: (serviceIds?.length ? serviceIds.split(',') : []).filter(
        (serviceId) => {
          return Object.keys(filterOptions.serviceIds).includes(serviceId);
        },
      ),
    };

    const roleLocationFilter = createRoleLocationFilter({
      filters,
      roles:
        currentRolesLocation?.roles.filter((role) => {
          const { service = '' } = parseRoleId(role.id);
          return Object.keys(filterOptions.serviceIds).includes(service);
        }) ?? [],
    });

    const filteredRoleLocations = [
      ...selfRoleLocations,
      ...inheritedRoleLocations,
      ...groupRoleLocations,
    ].filter(roleLocationFilter);

    const roleAssignments = {
      assignments: iamPaginateArray(filteredRoleLocations, {
        currentPage: Number(rolesCurrentPage),
        currentPageSize: Number(rolesCurrentPageSize),
      }),
      currentPage: rolesCurrentPage,
      currentPageSize: rolesCurrentPageSize,
      totalItems: filteredRoleLocations.length,
    };

    const groupIds = [
      ...new Set(
        roleAssignments.assignments
          .filter(({ member }) => {
            return (
              member.memberType ===
              HashicorpCloudResourcemanagerPolicyBindingMemberType.GROUP
            );
          })
          .map(({ member }) => {
            return member.memberId as string;
          }),
      ),
    ] as string[];

    const fetchAllGroups = iamFetchUntilEmpty(
      (orgId, unused, groupIds, paginationCount) =>
        this.api.groups.groupsServiceListGroups(
          orgId,
          unused,
          groupIds,
          paginationCount,
        ),
      'groups',
    );

    const { groups = [] } =
      groupIds && groupIds?.length
        ? await fetchAllGroups(
            `organization/${organization?.id}`,
            undefined,
            groupIds,
            100,
          )
        : {};

    return {
      filters,
      filterOptions,
      groups,
      groupRoleLocations,
      inheritedRoleLocations,
      roleAssignments,
    };
  }
}
